import { forwardRef, ChangeEvent } from 'react'
import { InputLabel, Box, TextField as MTextField, TextFieldProps, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

type Props = TextFieldProps & {
  maxLength?: number
  setValueAs?: (value: string) => string
}

const StyledTextField = styled(MTextField)`
  .MuiInputBase-root {
    background-color: ${({ theme }) => theme.palette.common.white};

    &.Mui-disabled {
      color: ${({ theme }) => theme.custom.colors.common.grey1};
    }
  }

  .MuiOutlinedInput-root {
    .MuiOutlinedInput-notchedOutline {
      border-width: 2px;
    }

    &.Mui-disabled .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.custom.colors.common.grey};
    }

    &:not(.Mui-error).Mui-focused:not(.Mui-disabled),
    &:not(.Mui-error):hover:not(.Mui-disabled) {
      .MuiOutlinedInput-notchedOutline {
        border-color: ${({ theme }) => theme.custom.colors.common.green};
      }
    }

    .MuiOutlinedInput-input {
      padding: 10px 13px 13px;
    }
  }
` as typeof MTextField

const StyledLabel = styled(Typography)`
  &.MuiTypography-caption.Mui-disabled {
    color: ${({ theme }) => theme.custom.colors.common.black};
  }
`

export const TextField = forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { label, maxLength, onChange, setValueAs, slotProps, ...rest } = props
  const { error, value, disabled } = rest
  const typoClassName = [disabled ? 'Mui-disabled' : false].filter(Boolean).join(' ')
  const { htmlInput, inputLabel, ...slotPropsRest } = slotProps || {}

  const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    if (typeof setValueAs === 'function' && event) {
      event.target.value = setValueAs(event.target.value)
    }
    if (typeof onChange === 'function') {
      onChange(event)
    }
  }

  const showLetterCount = maxLength !== undefined && typeof value === 'string'

  return (
    <Box>
      <InputLabel error={error} {...(inputLabel as any)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
          <StyledLabel className={typoClassName} variant="caption">
            {label}
          </StyledLabel>
          {showLetterCount && (
            <Typography className={typoClassName} variant="body2">
              {value.length} of {maxLength}
            </Typography>
          )}
        </Box>
        <StyledTextField
          ref={ref}
          slotProps={{
            htmlInput: {
              onChange: onChangeInput,
              ...htmlInput,
            },
            formHelperText: {
              variant: 'standard',
            },
            ...slotPropsRest,
          }}
          {...rest}
        />
      </InputLabel>
    </Box>
  )
})
